import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useWebSocket } from "../context/WebSocketContext";
import { getHintBank } from "./hintBank";
import { fetchHintBank } from "./hintApi";
import { loadGmHudConfig } from "../utils/config";
import { estimateProgress } from "./progress";

// HintContext glues the hint panel to the chat (SCRUM-686 mockup).
//
// It sits inside the WebSocketProvider (for the flow + game clock) and above both the HintPanel
// and the ChatInterface, so a hint picked in the panel can land in the chat's input box, and a
// message sent from the chat can be reported back as "that hint went out". Nothing here is
// persisted yet — in the real feature `sentHints` becomes the structured activity log row.

const HintContext = createContext(null);

const IS_DEV = process.env.NODE_ENV === "development";

// normLang maps whatever the controller or a button says ("EN", "english", "da") to "da"/"en".
export function normLang(l) {
    if (!l) return null;
    const s = String(l).toLowerCase();
    if (s.startsWith("en")) return "en";
    if (s.startsWith("da")) return "da";
    return null;
}

// allSolved reduces a prop's per-part status (the scale reports one flag per weight) to one
// answer: null when unknown, true only when every part is solved.
function allSolved(parts) {
    if (parts === null || parts === undefined) return null;
    const list = Array.isArray(parts) ? parts : Object.values(parts);
    if (list.length === 0) return null;
    return list.every(p => p === true || p?.solved === true);
}

// hintText picks the hint's text in the chosen language, falling back to Danish when the
// English one has not been written yet.
export function hintText(hint, language) {
    if (!hint) return "";
    if (language === "en" && hint.en && hint.en.trim() && !hint.en.trim().startsWith("[")) return hint.en;
    return hint.text || "";
}

export function HintProvider({ children }) {
    const {
        gameFlow, elapsedTime, timerIsRunning, gameDetailConfig, gameLanguage, sendLanguage,
        weightsSolved, telegraphAllSolved, telegraphConnected, letterSolved, radioCurrentCorrect, clockAllSolved, pictureSolved, pictureConnected,
        gameControllerConnected,
    } = useWebSocket();
    const { gameId: routeId } = useParams();
    const gameId = gameDetailConfig?.game_id;

    // Hint language. Rooms with a game-controller report the language the game was started in
    // (gameLanguage, "EN"/"DA") and that always wins while it is reported: switching in the panel
    // tells the controller (sendLanguage) and the panel follows what the room then says, so props
    // and hints never disagree. Rooms without a controller only have the panel's choice,
    // remembered per room in this browser until the timer is reset for the next team.
    const langKey = `hintLang:${routeId || gameId || ""}`;
    const [localLang, setLocalLang] = useState(() => {
        try { return localStorage.getItem(langKey) || null; } catch { return null; }
    });
    const language = normLang(gameLanguage) || normLang(localLang) || "da";
    const setLanguage = useCallback((l) => {
        const lang = normLang(l) || "da";
        setLocalLang(lang);
        try { localStorage.setItem(langKey, lang); } catch { /* private mode */ }
        if (normLang(gameLanguage) && normLang(gameLanguage) !== lang && typeof sendLanguage === "function") sendLanguage(lang);
    }, [langKey, gameLanguage, sendLanguage]);
    const clearLocalLang = useCallback(() => {
        setLocalLang(null);
        try { localStorage.removeItem(langKey); } catch { /* private mode */ }
    }, [langKey]);
    // The bank bundled in the image is the fallback; the live one comes from games-cloud (where
    // the Admin editor writes). Keyed by the config's game_id; rooms whose config has none are
    // resolved through the bundled bank's aliases first.
    const bundled = useMemo(() => getHintBank(gameId, routeId), [gameId, routeId]);
    // Try the room's own game_id first, then the bank it shares through the bundled aliases
    // (Flugten 1–6 share "flugten_6"). A room may later get a bank of its own without a deploy.
    const sharedKey = bundled?.gameId || null;
    const [remote, setRemote] = useState(null);
    useEffect(() => {
        let cancelled = false;
        setRemote(null);
        const keys = [...new Set([gameId, sharedKey].filter(Boolean))];
        if (keys.length === 0) return undefined;
        (async () => {
            try {
                for (const key of keys) {
                    const row = await fetchHintBank(key);
                    if (cancelled) return;
                    if (row?.bank?.riddles?.length) { setRemote(row.bank); return; }
                }
            } catch (err) {
                console.warn("[hints] using bundled bank:", err.message);
            }
        })();
        return () => { cancelled = true; };
    }, [gameId, sharedKey]);
    // Production only trusts games-cloud (so a room is live exactly when its bank is enabled
    // there); the bundled bank is a development convenience.
    const loadedBank = remote || (IS_DEV ? bundled : null);

    // Rollout gates (SCRUM-686): the room's bank must be enabled, and the user must have the
    // "Hints" tick in the staff sheet. Development skips the user gate (the local test token
    // has no sheet row, and the sheet column may not exist yet); the room gate still applies.
    const [userAllowed, setUserAllowed] = useState(IS_DEV);
    useEffect(() => {
        if (IS_DEV) return undefined;
        let cancelled = false;
        loadGmHudConfig().then(cfg => {
            if (cancelled) return;
            const email = (localStorage.getItem("userEmail") || "").toLowerCase();
            setUserAllowed(!!cfg?.users?.[email]?.hints);
        });
        return () => { cancelled = true; };
    }, []);
    const roomEnabled = !!loadedBank && loadedBank.enabled !== false;
    const bank = roomEnabled && userAllowed ? loadedBank : null;
    const [sentHints, setSentHints] = useState([]);
    const [playerMessages, setPlayerMessages] = useState([]);
    const [override, setOverride] = useState(null);
    const [draft, setDraft] = useState(null);

    // Demo-only knobs so the mockup can be driven without a game-controller or a running timer.
    const [simElapsedSec, setSimElapsedSec] = useState(0);
    const [simSolved, setSimSolved] = useState([]);

    const realElapsed = elapsedTime != null ? Math.round(parseFloat(elapsedTime)) : null;
    const elapsedSec = realElapsed != null && timerIsRunning ? realElapsed + simElapsedSec : (simElapsedSec || realElapsed);

    // Rooms without a flow (Night Train) still report their props one by one. Translate the
    // statuses the WebSocket context already parses into flow-style {id, solved} entries, keyed
    // by the bank's riddle ids, so the estimator treats them like Spymaster's flow. Only props
    // that are connected (status not null) count; a disconnected prop is simply unknown.
    // The radio reports "currently on the right frequency", which flips back when the team
    // turns the dial, so it is latched: once heard, the riddle stays solved for the session.
    const [radioHeard, setRadioHeard] = useState(false);
    useEffect(() => { if (radioCurrentCorrect === true) setRadioHeard(true); }, [radioCurrentCorrect]);
    const rawSensors = useMemo(() => {
        const out = [];
        // The context's initial values are false, not null, so without a controller on the line
        // every prop would look like "connected and unsolved". No controller: no sensor at all.
        if (!gameControllerConnected) return out;
        const add = (id, solved) => { if (solved !== null && solved !== undefined) out.push({ id, solved: !!solved }); };
        // The scale sits in Missy's safe: all three weights hit = the safe riddle is solved. The
        // dice suitcase ("johnny_terninger") is a dumb prop that reports nothing.
        add("marriott_pengeskab", allSolved(weightsSolved));
        add("johnny_uret", clockAllSolved);
        add("missy_brevholder", letterSolved);
        add("elliot_pensel", pictureConnected ? pictureSolved : null);
        // The telegraph's solved flag is not cleared when it disconnects (only its connected flag
        // flips), so an offline telegraph would otherwise read as "connected and unsolved".
        add("elliot_morse", telegraphConnected ? telegraphAllSolved : null);
        add("george_radio", radioCurrentCorrect === null || radioCurrentCorrect === undefined ? (radioHeard ? true : null) : (radioHeard || radioCurrentCorrect));
        return out;
    }, [gameControllerConnected, weightsSolved, clockAllSolved, letterSolved, pictureConnected, pictureSolved, telegraphConnected, telegraphAllSolved, radioCurrentCorrect, radioHeard]);

    // The props latch "solved" in firmware and only clear it on a power cycle — and a prop whose
    // pieces are left in place (weights in the safe, the letter in its holder) re-latches within
    // a second of powering up. So a prop that says "solved" from the moment we first see it is
    // not evidence of anything this team did. A prop only counts as solved once this HUD has
    // seen it unsolved first; until then it is unknown. Seen-unsolved is forgotten on timer reset.
    const [seenUnsolved, setSeenUnsolved] = useState(() => new Set());
    useEffect(() => {
        const fresh = rawSensors.filter(s => !s.solved && !seenUnsolved.has(s.id)).map(s => s.id);
        if (fresh.length) setSeenUnsolved(prev => new Set([...prev, ...fresh]));
    }, [rawSensors, seenUnsolved]);
    const sensorFlow = useMemo(
        () => rawSensors.filter(s => !s.solved || seenUnsolved.has(s.id)),
        [rawSensors, seenUnsolved],
    );

    const flow = useMemo(() => {
        if (gameFlow && gameFlow.length > 0) return gameFlow;
        if (sensorFlow.length > 0) return sensorFlow;
        if (IS_DEV && simSolved.length > 0 && bank) {
            // Fake a flow where only the riddles ticked in the demo row report in — like a room
            // with sensor modules on some props and not others.
            return bank.riddles.filter(r => simSolved.includes(r.id)).map(r => ({ id: r.id, solved: true }));
        }
        return [];
    }, [gameFlow, sensorFlow, simSolved, bank]);

    const progress = useMemo(() => {
        if (!bank) return null;
        return estimateProgress({
            riddles: bank.riddles,
            linear: bank.linear,
            flow,
            elapsedSec,
            sentHints,
            playerMessages,
            override,
        });
    }, [bank, flow, elapsedSec, sentHints, playerMessages, override]);

    // How long have they been on the current riddle? We only learn transitions client-side, so
    // remember the clock reading when the guess last moved. State (not a ref) so the panel
    // re-renders with the new baseline the moment the riddle changes.
    const currentId = progress?.currentId ?? null;
    const [currentSince, setCurrentSince] = useState({ id: null, elapsed: 0 });
    useEffect(() => {
        if (currentSince.id !== currentId) {
            setCurrentSince({ id: currentId, elapsed: elapsedSec || 0 });
        }
    }, [currentId, elapsedSec, currentSince.id]);
    const secondsOnCurrent = elapsedSec != null && currentSince.id === currentId
        ? Math.max(0, elapsedSec - currentSince.elapsed)
        : 0;

    // A timer reset (elapsed drops to ~0) starts a new team: wipe everything the last team left,
    // including the radio latch and the panel's own language choice.
    const lastElapsedRef = useRef(elapsedSec);
    useEffect(() => {
        if (lastElapsedRef.current != null && elapsedSec != null && elapsedSec < lastElapsedRef.current - 60) {
            setSentHints([]);
            setPlayerMessages([]);
            setOverride(null);
            setRadioHeard(false);
            setSeenUnsolved(new Set());
            clearLocalLang();
            setSimSolved([]);
            setSimElapsedSec(0);
        }
        lastElapsedRef.current = elapsedSec;
    }, [elapsedSec, clearLocalLang]);

    // The GM's manual pick is a correction, not a lock: when a prop reports that riddle solved
    // AFTER the pick, hand the guess back to the signals. A riddle that was already solved when
    // the GM picked it stays picked — the GM must always be able to open any riddle's hints,
    // including one a sensor got wrong.
    const solvedIdsRef = useRef(new Set());
    useEffect(() => {
        const nowSolved = new Set(flow.filter(f => f.solved).map(f => f.id));
        if (override && nowSolved.has(override) && !solvedIdsRef.current.has(override)) setOverride(null);
        solvedIdsRef.current = nowSolved;
    }, [override, flow]);

    const pickHint = useCallback((riddle, hint) => {
        setDraft({ riddleId: riddle.id, riddleTitle: riddle.title, level: hint.level, text: hintText(hint, language), lang: language, pickedAt: Date.now() });
    }, [language]);

    // markSent is called by the chat once a message actually went out. `edited` tells the log
    // whether the GM rewrote the suggestion — that signal is what makes the bank better over time.
    const markSent = useCallback(({ riddleId, level, text, edited }) => {
        setSentHints(prev => [...prev, { riddleId, level, text, edited, at: Date.now(), elapsedSec }]);
        setDraft(null);
        // A sent hint is the GM saying "they are here": drop any manual pick that disagrees.
        setOverride(prev => (prev && prev !== riddleId ? null : prev));
    }, [elapsedSec]);

    const notePlayerMessage = useCallback((text, at) => {
        setPlayerMessages(prev => [...prev.slice(-49), { text, at: at || Date.now() }]);
    }, []);

    const value = useMemo(() => ({
        bank,
        progress,
        elapsedSec,
        secondsOnCurrent,
        draft,
        language,
        setLanguage,
        languageFromController: !!normLang(gameLanguage),
        pickHint,
        clearDraft: () => setDraft(null),
        markSent,
        notePlayerMessage,
        override,
        setOverride,
        sentHints,
        demo: IS_DEV ? {
            addMinutes: m => setSimElapsedSec(s => Math.max(0, s + m * 60)),
            solveNext: () => {
                if (!bank) return;
                setSimSolved(prev => {
                    const next = bank.riddles.find(r => !prev.includes(r.id) && !(flow.find(f => f.id === r.id)?.solved));
                    return next ? [...prev, next.id] : prev;
                });
            },
            fakePlayerMessage: text => notePlayerMessage(text),
            reset: () => { setSimSolved([]); setSimElapsedSec(0); setSentHints([]); setPlayerMessages([]); setOverride(null); },
            simSolved,
            simElapsedSec,
        } : null,
    }), [bank, progress, elapsedSec, secondsOnCurrent, draft, language, setLanguage, gameLanguage, pickHint, markSent, notePlayerMessage, override, sentHints, simSolved, simElapsedSec, flow]);

    return <HintContext.Provider value={value}>{children}</HintContext.Provider>;
}

// useHints returns null outside a HintProvider, so ChatInterface can be used without one.
export function useHints() {
    return useContext(HintContext);
}
